import { Dialog, Grid, Slide, useMediaQuery } from "@mui/material"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import SpeedDial from "@mui/material/SpeedDial"
import SpeedDialAction from "@mui/material/SpeedDialAction"
import SpeedDialIcon from "@mui/material/SpeedDialIcon"
import { DataGrid } from "@mui/x-data-grid"
import { getAnalytics } from "firebase/analytics"
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore"
import { FunctionComponent, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import Color from "../Color"
import {
  getEmptyInventory,
  getInventoryFromDocSnapshot,
} from "../contexts/InventoryContext"
import Dimen from "../Dimen"
import { Collection, Field } from "../Enum"
import {
  paperStyleDefault,
  redGreenCellColor,
  useSpeedDialStyle,
} from "../Style"
import { timber_debug } from "../Timber"
import { Inventory } from "../Type"
import {
  getInventoryListColumns,
  getTranslatedDataGridToolbar,
} from "../utils/DataGridUtils"
import { exportInventoriesAsExcel } from "../utils/ExcelUtils"
import { exportInventoryListAsPdf } from "../utils/PdfUtils"
import {
  Action,
  getInventoryListSpeedDialActions,
} from "../utils/SpeedDialUtils"
import InventoryDialog from "./InventoryDialog"

const InventoryList: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const intl = useIntl()
  // Keeps action tooltip text in a single line
  const classes = useSpeedDialStyle()
  const minHeight = useMediaQuery("(min-height:700px)")
  const analytics = getAnalytics()

  const [inventories, setInventories] = useState<Inventory[]>([])

  // Inventory dialog states
  const [showInventoryDialog, setShowInventoryDialog] = useState<boolean>(false)
  const [inventory, setInventory] = useState<Inventory>(getEmptyInventory())

  // Firestore
  const firestore = getFirestore()
  const inventoriesRef = collection(
    firestore,
    `/${Collection.Users}/${userId}/${Collection.Inventories}`
  )
  const inventoriesQuery = query(inventoriesRef, orderBy(Field.Name))

  useEffect(() => {
    // Sets up real-time Inventories listener
    timber_debug("useEffect subscribeInventoriesListener")
    const unsubscribeInventoriesListener = onSnapshot(
      inventoriesQuery,
      (querySnapshot) => {
        const newInventories: Inventory[] = []
        querySnapshot.forEach((doc) => {
          newInventories.push(getInventoryFromDocSnapshot(doc))
        })
        timber_debug(`Downloaded ${newInventories.length} inventories`)
        setInventories(newInventories)
      },
      (error) => {
        console.error("Error fetching inventories", error)
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeInventoriesListener")
      unsubscribeInventoriesListener()
    }
  }, [])

  const onSelectInventory = (inventory: Inventory) => {
    setInventory(inventory)
    setShowInventoryDialog(true)
  }

  const openInventoryDialog = () => {
    // Reset inventory before opening dialog
    setInventory(getEmptyInventory())
    setShowInventoryDialog(true)
  }

  const handleFabAction = (actionId: string) => {
    switch (actionId) {
      case "inventory":
        openInventoryDialog()
        break
      case "pdf":
        exportInventoryListAsPdf(inventories, analytics)
        break
      case "excel":
        exportInventoriesAsExcel(inventories, analytics)
        break
      default:
        break
    }
  }

  return (
    <Box
      height="91vh"
      display="flex"
      flexDirection="column"
      sx={redGreenCellColor}
    >
      <Paper
        elevation={3}
        style={{
          height: "85vh",
          margin: "8px",
          ...paperStyleDefault,
          padding: "8px",
        }}
      >
        <div style={{ fontSize: Dimen.FontSizeDefault }}>
          <FormattedMessage id="nav.inventory.list" />
        </div>
        <Grid
          container
          spacing={minHeight ? 1 : 0}
          direction={"row"}
          height="85vh"
          marginTop={1}
        >
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            {/* Inventory list */}
            <div style={{ height: minHeight ? "85%" : "100%" }}>
              <DataGrid
                localeText={getTranslatedDataGridToolbar(intl)}
                onRowClick={(params) =>
                  onSelectInventory(params.row as Inventory)
                }
                rows={inventories}
                columns={getInventoryListColumns(intl)}
                pageSizeOptions={[10]}
                disableRowSelectionOnClick
                density="standard"
                showToolbar
              />
            </div>
          </Grid>
        </Grid>
        {/* Action button */}
        <SpeedDial
          ariaLabel="InventoryList SpeedDial"
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
          }}
          icon={<SpeedDialIcon />}
          FabProps={{
            style: { backgroundColor: Color.Red40, color: Color.White },
          }}
        >
          {getInventoryListSpeedDialActions(intl).map((action: Action) => (
            <SpeedDialAction
              key={action.id}
              icon={action.icon}
              slotProps={{
                tooltip: {
                  title: action.name,
                  open: true,
                },
                fab: {
                  style: {
                    backgroundColor: Color.Primary,
                    color: Color.Black,
                  },
                },
              }}
              classes={classes} // for keep action tooltip text in a single line
              onClick={() => {
                handleFabAction(action.id)
              }}
            />
          ))}
        </SpeedDial>
        {/* Inventory dialog */}
        <Dialog
          open={showInventoryDialog}
          slots={{ transition: Slide }}
          slotProps={{
            transition: {
              direction: "up",
            },
          }}
          keepMounted
          onClose={(_, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") {
              setShowInventoryDialog(false)
            }
          }}
        >
          <InventoryDialog
            userId={userId}
            inventory={inventory}
            onClickClose={() => {
              setShowInventoryDialog(false)
            }}
          ></InventoryDialog>
        </Dialog>
      </Paper>
    </Box>
  )
}

export default InventoryList
